import React, { useEffect } from 'react';

interface TierSelectorProps {
  tiers: any[];
  isOpen: boolean;
  onClose: () => void;
  onSelectTier: (tierIdx: number) => void;
  onRemoveFromTier?: () => void;
  isInTier: boolean;
  position: { x: number, y: number };
  currentTierIdx?: number | null;
  onReorderInTier?: () => void;
}

/**
 * A popup menu for selecting which tier to move an item to
 */
export default function TierSelector({ 
  tiers, 
  isOpen, 
  onClose, 
  onSelectTier, 
  onRemoveFromTier,
  isInTier,
  position,
  currentTierIdx,
  onReorderInTier
}: TierSelectorProps) {
  useEffect(() => {
    // console.log('[TierSelector] Component rendered, isOpen:', isOpen);
  }, [isOpen]);

  if (!isOpen) return null;

  // Close when clicking outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      // console.log('[TierSelector] Backdrop clicked, closing');
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  // Handle tier selection
  const handleTierSelect = (e: React.MouseEvent, tierIdx: number) => {
    // console.log('[TierSelector] Tier selected:', tierIdx);
    e.preventDefault();
    e.stopPropagation();
    onSelectTier(tierIdx);
  };

  // Handle removal
  const handleRemove = (e: React.MouseEvent) => {
    // console.log('[TierSelector] Remove button clicked');
    e.preventDefault();
    e.stopPropagation();
    if (onRemoveFromTier) {
      onRemoveFromTier();
    }
  };

  // Handle reorder in current tier
  const handleReorder = (e: React.MouseEvent) => {
    // console.log('[TierSelector] Reorder in tier button clicked');
    e.preventDefault();
    e.stopPropagation();
    if (onReorderInTier) {
      onReorderInTier();
    }
  };

  // Calculate position, keeping the popup within viewport
  const posX = Math.min(position.x, window.innerWidth - 200);
  const posY = Math.min(position.y, window.innerHeight - (tiers.length * 48 + 60));

  return (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      <div 
        className="absolute bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden"
        style={{
          left: `${posX}px`,
          top: `${posY}px`,
          width: '180px',
          maxWidth: '90vw',
        }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks on the container from bubbling
      >
        <div className="p-3 border-b border-gray-700 bg-gray-900">
          <h3 className="text-sm font-medium text-white">Move to tier:</h3>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {tiers.map((tier, index) => (
            // If this is the current tier and reordering is enabled, show "Reorder" instead
            (index === currentTierIdx && onReorderInTier) ? (
              <button
                key={`tier-select-${index}`}
                className="w-full px-4 py-2 text-left hover:bg-blue-700 text-blue-400 flex items-center"
                onClick={handleReorder}
              >
                <div 
                  className="w-4 h-4 mr-2 rounded" 
                  style={{ backgroundColor: tier.color }} 
                />
                <span>Reorder in {tier.name} Tier</span>
              </button>
            ) : (
              <button
                key={`tier-select-${index}`}
                className="w-full px-4 py-2 text-left hover:bg-gray-700 text-gray-200 flex items-center"
                onClick={(e) => handleTierSelect(e, index)}
              >
                <div 
                  className="w-4 h-4 mr-2 rounded" 
                  style={{ backgroundColor: tier.color }} 
                />
                <span>{tier.name} Tier</span>
              </button>
            )
          ))}
          
          {isInTier && onRemoveFromTier && (
            <button
              className="w-full px-4 py-2 text-left hover:bg-red-900 text-red-400 border-t border-gray-700"
              onClick={handleRemove}
            >
              Remove from tier
            </button>
          )}
        </div>
      </div>
    </div>
  );
} 